/* CUSTOM_VGUX_BY_CLAUDE (Ryan 2026-10-11, Vendor Guide UX batch, push 2) - THE PO PROFILE FORM, TIDIED.

   custom-vendor-guide.js still renders every PO Profile field exactly as before; this file only REARRANGES what it
   rendered, so every `data-vg-po-field` input keeps its name, its value and its place in collectPoFromForm, and every
   existing change handler still sees the same elements. What it adds:
     #9  the fields grouped into cards, one per section of the Product Rules "Vendor Guide complete" checklist
         (lib/brand-checklist.js GUIDE_SECTIONS in rfq-worker), plus Pallet fee and "What we write to them";
     #12 each card says "N of M answered" for the checklist questions it holds, live as you type; every card starts
         folded (Ryan 2026-10-11: the pills say enough) and a card that does not apply says why; the email / instruction
         wording sits in one card. ⛔ NOTHING WITH A VALUE IS EVER HIDDEN: folding is one click from open, and a field is only tucked away
         when it is both empty and does not apply;
     #5  a long hint is cut to its first sentence, the rest behind a "(?)" tip;
     #6/#7 every dropdown becomes our own: short option names that fit, and hovering an option shows what it means in
         a panel beside the list. The real <select> stays (hidden) and is what saves - a pick sets it and fires its
         change event, so the existing show / hide rules keep working;
     #11 the instruction lines show **bold** and *italic* the way the PO PDF prints them, and Ctrl+B / Ctrl+I mark the
         selected words in the box you type a line into.
   Any PO field this file does not know lands in an "Other" card rather than vanishing.

   Called by custom-vendor-guide.js renderModal() as window.VGPoForm.enhance(modalBody) for an admin's edit / new. */
(function () {
  'use strict';

  var PO_WORKFLOWS = ['po-only', 'freight-then-po'];
  var WEARRANGE = ['we-arrange', 'we-arrange-quote'];
  var SUPPLIER_SHIPS_MODELS = ['parcel-only', 'freight-in-product', 'flat-rate'];

  var CARDS = [
    { id: 'basics', title: 'The basics', note: 'Who they are on our paperwork.',
      fields: ['shortCode', 'organization', 'alternateFor', 'ordersThrough', 'quoteAliases', 'aliasKeywords'] }, // ordersThrough: CUSTOM_ORDERSTHROUGH_BY_CLAUDE
    { id: 'ordering', title: 'How we order', note: 'What has to happen before a PO goes, and what we wait for after.',
      fields: ['workflow', 'postPoWait', 'poFollowupDays', 'etaFollowupDays'] },
    { id: 'po', title: 'Where purchase orders go', note: 'Who gets the PO, who it greets, and the address printed on it.',
      fields: ['contact.email', 'contact.cc', 'contact.bcc', 'poFrom', 'contact.ordersContactFirstName', 'contact.ordersGreeting',
        'address.street', 'address.cityStateZip', 'contact.attention', 'contact.phone'] },
    { id: 'portal', title: 'Their dealer portal', note: 'Where we order or pay online, and which stored login gets us in.',
      fields: ['dealerPortalUrl', 'dealerPortalAccess', 'dealerPortalLogin'] },
    { id: 'pay', title: 'How we pay', note: 'How the bill gets settled.',
      fields: ['payMethod', 'paymentTerms', 'settleMode', 'costsAreEstimates'] },
    { id: 'ship', title: 'How orders ship', note: 'Who moves the goods, and what the agents do about freight.',
      fields: ['shippingModel', 'shippingFlatRate', 'flatRateBasis', 'shippingNote', 'freightMode', 'lockedRateBasis', 'freightBilling',
        'freeShippingOnline', 'maxPalletLb'] },
    { id: 'pallet', title: 'Pallet / packaging fee', note: 'Only when Blue Sky brokers the freight.',
      fields: ['palletFee', 'palletFeeBasis', 'palletFeeLabel', 'palletFeeExclusions'] },
    { id: 'freight-asks', title: 'Freight and dims asks', note: 'Where the freight-quote and weight & dims emails go. Blank uses the PO email above.',
      fields: ['freightQuoteTo', 'freightQuoteCc', 'freightQuoteBcc', 'freightDimsTo', 'freightDimsCc', 'freightDimsBcc'] },
    { id: 'wording', title: 'What we write to them', note: 'Optional. A blank box uses the house wording.',
      fields: ['defaultInstructionsVendor', 'defaultInstructionsBsfs', 'emailSubject', 'emailBodyVendor', 'emailBodyBsfs',
        'freightQuoteSubject', 'freightQuoteBody', 'freightDimsSubject', 'freightDimsBody'] },
  ];

  // The freight-ask boxes all read "Subject" / "Body" / "To" inside their old fieldset; out of it they need their ask.
  var RELABEL = {
    freightQuoteTo: 'Freight-quote ask: To', freightQuoteCc: 'Freight-quote ask: CC', freightQuoteBcc: 'Freight-quote ask: BCC',
    freightDimsTo: 'Weight & dims ask: To', freightDimsCc: 'Weight & dims ask: CC', freightDimsBcc: 'Weight & dims ask: BCC',
    freightQuoteSubject: 'Freight-quote ask: subject', freightQuoteBody: 'Freight-quote ask: body',
    freightDimsSubject: 'Weight & dims ask: subject', freightDimsBody: 'Weight & dims ask: body',
  };

  /* ---------- pure helpers (tested in tests/vendor-guide-po-form-tests.mjs) ---------- */

  function has(v) { return v != null && String(v).trim() !== ''; }

  // "Standard — we ship it (default)" -> {name:'Standard', why:'We ship it (default)'}. A parenthesis explains only when
  // it is words ("Net terms (they invoice, we pay later)"), never an address ("Me (ryan@...)").
  function splitOption(text) {
    var t = String(text || '').replace(/\s+/g, ' ').trim();
    var m = t.match(/^(.+?)\s+[—–-]\s+(.+)$/);
    if (!m) {
      var p = t.match(/^(.+?)\s*\(([^()]*\s[^()]*)\)$/);
      if (p && p[2].indexOf('@') === -1) m = p;
    }
    if (!m) return { name: t, why: '' };
    var why = m[2].trim();
    return { name: m[1].trim(), why: why.charAt(0).toUpperCase() + why.slice(1) };
  }

  // The first sentence of a hint. "e.g." and "i.e." are not sentence ends.
  function firstSentence(text) {
    var t = String(text || '').replace(/\s+/g, ' ').trim();
    var re = /[.!?](?=\s|$)/g, m;
    while ((m = re.exec(t))) {
      var before = t.slice(Math.max(0, m.index - 3), m.index + 1).toLowerCase();
      if (/e\.g\.$|i\.e\.$|\be\.g$|\bi\.e$/.test(before) || /(^|\s)[a-z]\.$/.test(t.slice(Math.max(0, m.index - 2), m.index + 1).toLowerCase())) continue;
      return t.slice(0, m.index + 1);
    }
    return t;
  }

  // **bold**, *italic*, ***both*** -> HTML, after escaping. The same marks the PO PDF prints (rfq po-pdf.js
  // instructionSegments); a lone asterisk stays an asterisk.
  function esc(s) { return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); }
  function markupHtml(line) {
    return esc(line).replace(/\*\*\*([^*]+)\*\*\*|\*\*([^*]+)\*\*|\*([^*\s][^*]*?)\*/g, function (all, bi, b, i) {
      if (bi != null) return '<strong><em>' + bi + '</em></strong>';
      if (b != null) return '<strong>' + b + '</strong>';
      return '<em>' + i + '</em>';
    });
  }

  // Wrap the selection [a, b) of `value` in a mark; an empty selection drops the pair and puts the caret inside.
  function wrapMark(value, a, b, mark) {
    var v = String(value || '');
    var sel = v.slice(a, b);
    var lead = sel.match(/^\s*/)[0], trail = sel.match(/\s*$/)[0];
    var core = sel.slice(lead.length, sel.length - trail.length);
    if (!core) return { value: v.slice(0, a) + mark + mark + v.slice(b), start: a + mark.length, end: a + mark.length };
    // Already marked with exactly this mark: take it off.
    if (v.slice(a + lead.length - mark.length, a + lead.length) === mark && v.slice(b - trail.length, b - trail.length + mark.length) === mark) {
      var s0 = a + lead.length - mark.length, e0 = b - trail.length + mark.length;
      return { value: v.slice(0, s0) + core + v.slice(e0), start: s0, end: s0 + core.length };
    }
    var out = v.slice(0, a) + lead + mark + core + mark + trail + v.slice(b);
    return { value: out, start: a + lead.length + mark.length, end: a + lead.length + mark.length + core.length };
  }

  /* The checklist questions each card holds, from the form's current values. Mirrors rfq-worker
     lib/brand-checklist.js GUIDE_ITEMS (the PO Profile ones); the server stays the judge, this is the live count.
     Returns {applies, reason, questions:[bool...]} per card id. */
  function cardStatus(val) {
    var wf = val('workflow'), model = val('shippingModel') || 'standard', fm = val('freightMode');
    var supplierShips = SUPPLIER_SHIPS_MODELS.indexOf(model) !== -1 || fm === 'vendor-ships';
    var poWf = PO_WORKFLOWS.indexOf(wf) !== -1;
    var portal = wf === 'web-order' || has(val('dealerPortalUrl'));
    var asks = WEARRANGE.indexOf(fm) !== -1 || wf === 'freight-then-po';
    var locked = true;
    if (fm === 'locked-rate') {
      var basis = val('lockedRateBasis');
      locked = basis === 'per-order' ? Number(val('lockedRate')) > 0 : (basis === 'per-product' ? has(val('lockedRates')) : false);
    }
    var ship = [has(model), has(fm) || supplierShips];
    if (fm === 'locked-rate') ship.push(locked);
    else if (model === 'flat-rate' && val('flatRateBasis') === 'per-product') ship.push(has(val('lockedRates'))); // CUSTOM_PERPRODUCTFREIGHT_BY_CLAUDE
    if (!supplierShips) ship.push(has(val('freightBilling')));
    return {
      basics: { applies: true, questions: [has(val('shortCode'))] },
      ordering: { applies: true, questions: [has(wf)] },
      po: { applies: poWf, reason: wf === 'web-order' ? 'We order on their website, so no PO goes to them.' : 'Pick how we order first.',
        questions: [has(val('contact.email')), has(val('address.street')) && has(val('address.cityStateZip')),
          has(val('contact.ordersContactFirstName')) || has(val('contact.ordersGreeting')), has(val('poFrom'))] },
      portal: { applies: portal, reason: 'Only for a supplier we order from or pay through online.',
        questions: [has(val('dealerPortalUrl')), has(val('dealerPortalAccess')), has(val('dealerPortalLogin'))] },
      pay: { applies: true, questions: [has(val('payMethod')), has(val('paymentTerms')), has(val('settleMode')), has(val('costsAreEstimates'))] },
      ship: { applies: true, questions: ship },
      pallet: { applies: !supplierShips || Number(val('palletFee')) > 0, reason: 'They ship it themselves, so no pallet fee is charged.', questions: [] },
      'freight-asks': { applies: asks, reason: 'Only when we ask them for freight quotes or weights (Freight first, or We arrange).',
        questions: [has(val('freightQuoteTo')) || has(val('freightDimsTo')) || has(val('contact.email'))] },
      wording: { applies: true, questions: [] },
    };
  }

  function countText(st) {
    if (!st.applies) return { text: 'Not needed', cls: 'na' };
    if (!st.questions.length) return { text: 'Optional', cls: 'opt' };
    var n = st.questions.filter(Boolean).length;
    return { text: (n === st.questions.length ? '✓ ' : '') + n + ' of ' + st.questions.length + ' answered', cls: n === st.questions.length ? 'ok' : 'gap' };
  }

  /* ---------- the DOM work ---------- */

  function fieldEl(root, f) { return root.querySelector('[data-vg-po-field="' + f + '"]'); }
  function readVal(root) {
    return function (f) {
      var el = fieldEl(root, f);
      if (!el) return '';
      if (el.type === 'checkbox') return el.checked ? 'true' : '';
      return String(el.value || '').trim();
    };
  }

  // The piece of the old layout that holds one field: a whole row, or one column of a two / three-up row.
  function cellOf(el) {
    var row = el.closest('.vg-form__row');
    if (!row) return null;
    var split = row.classList.contains('vg-form__row--two') || row.classList.contains('vg-form__row--three');
    if (!split) return row;
    var c = el;
    while (c.parentElement && c.parentElement !== row) c = c.parentElement;
    return c.parentElement === row ? c : row;
  }

  var tipEl = null;
  function tip() {
    if (tipEl) return tipEl;
    tipEl = document.createElement('div');
    tipEl.className = 'vg-tip';
    tipEl.setAttribute('role', 'tooltip');
    tipEl.hidden = true;
    document.body.appendChild(tipEl);
    return tipEl;
  }
  function showTip(btn) {
    var t = tip();
    t.innerHTML = btn._vgTip || '';
    t.hidden = false;
    var r = btn.getBoundingClientRect();
    var w = Math.min(360, window.innerWidth - 24);
    t.style.width = w + 'px';
    var left = Math.min(Math.max(12, r.left + r.width / 2 - w / 2), window.innerWidth - w - 12);
    var top = r.bottom + 8;
    if (top + t.offsetHeight > window.innerHeight - 12) top = Math.max(12, r.top - 8 - t.offsetHeight);
    t.style.left = left + 'px';
    t.style.top = top + 'px';
  }
  function hideTip() { if (tipEl) tipEl.hidden = true; }

  // #5 - a hint longer than its first sentence keeps that sentence; the whole hint moves behind a (?) on the label.
  function shortenHints(card) {
    Array.prototype.slice.call(card.querySelectorAll('.vg-form__hint')).forEach(function (h) {
      if (h.classList.contains('vg-form__hint--inline') || h.classList.contains('vg-form__hint--block') || h._vgDone) return;
      h._vgDone = true;
      var text = (h.textContent || '').replace(/\s+/g, ' ').trim();
      var short = firstSentence(text);
      if (short.length >= text.length) return;
      var full = h.innerHTML;
      h.textContent = short;
      h.classList.add('vg-form__hint--one');
      var cell = h.parentElement;
      var label = null;
      var labels = cell ? cell.querySelectorAll('.vg-form__label, .vg-form__checkbox') : [];
      for (var i = 0; i < labels.length; i++) {
        if (labels[i].compareDocumentPosition(h) & 4) label = labels[i]; // the last label before this hint
      }
      var q = document.createElement('button');
      q.type = 'button';
      q.className = 'vg-qmark';
      q.setAttribute('aria-label', 'More about this');
      q.textContent = '?';
      q._vgTip = full;
      if (label && !label.querySelector('.vg-qmark')) label.appendChild(q);
      else h.appendChild(q);
    });
  }

  /* #6 / #7 - our own dropdown. */
  var openDd = null;
  function closeDd() {
    if (!openDd) return;
    var o = openDd; openDd = null;
    if (o.list.parentNode) o.list.parentNode.removeChild(o.list);
    if (o.why.parentNode) o.why.parentNode.removeChild(o.why);
    o.btn.setAttribute('aria-expanded', 'false');
    document.removeEventListener('mousedown', o.away, true);
    window.removeEventListener('scroll', o.onScroll, true);
    window.removeEventListener('resize', closeDd);
  }

  function makeSelect(select) {
    if (select._vgSel) return;
    select._vgSel = true;
    var wrap = document.createElement('div');
    wrap.className = 'vg-sel';
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'vg-form__input vg-sel__btn';
    btn.setAttribute('aria-haspopup', 'listbox');
    btn.setAttribute('aria-expanded', 'false');
    if (select.id) {
      btn.id = select.id + '-pick';
      var lab = document.querySelector('label[for="' + select.id + '"]');
      if (lab) lab.htmlFor = btn.id;
    }
    select.parentNode.insertBefore(wrap, select);
    wrap.appendChild(btn);
    wrap.appendChild(select);
    select.classList.add('vg-sel__native');
    select.tabIndex = -1;
    select.setAttribute('aria-hidden', 'true');

    function opts() {
      return Array.prototype.slice.call(select.options).map(function (o, i) {
        var s = splitOption(o.textContent);
        return { i: i, value: o.value, name: s.name, why: s.why };
      });
    }
    function paint() {
      var o = select.options[select.selectedIndex];
      btn.innerHTML = '<span class="vg-sel__val">' + esc(o ? splitOption(o.textContent).name : '') + '</span><span class="vg-sel__caret" aria-hidden="true">&#9662;</span>';
    }
    paint();
    select.addEventListener('change', paint);

    function choose(i) {
      if (select.selectedIndex !== i) {
        select.selectedIndex = i;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      paint();
      closeDd();
      btn.focus();
    }

    function open() {
      closeDd();
      var list = document.createElement('div');
      list.className = 'vg-dd';
      list.setAttribute('role', 'listbox');
      var why = document.createElement('div');
      why.className = 'vg-dd-why';
      why.hidden = true;
      var items = opts();
      list.innerHTML = items.map(function (o) {
        return '<button type="button" role="option" class="vg-dd__opt' + (o.i === select.selectedIndex ? ' vg-dd__opt--on' : '') +
          '" data-i="' + o.i + '" aria-selected="' + (o.i === select.selectedIndex) + '">' + esc(o.name) + '</button>';
      }).join('');
      document.body.appendChild(list);
      document.body.appendChild(why);
      var r = btn.getBoundingClientRect();
      list.style.minWidth = r.width + 'px';
      var top = r.bottom + 4;
      if (top + list.offsetHeight > window.innerHeight - 12 && r.top - 4 - list.offsetHeight > 12) top = r.top - 4 - list.offsetHeight;
      list.style.left = Math.min(r.left, window.innerWidth - list.offsetWidth - 12) + 'px';
      list.style.top = top + 'px';

      function explain(b) {
        var o = items[Number(b.getAttribute('data-i'))];
        if (!o || !o.why) { why.hidden = true; return; }
        why.textContent = o.why;
        why.hidden = false;
        var lr = list.getBoundingClientRect(), br = b.getBoundingClientRect();
        var w = Math.min(300, window.innerWidth - 24);
        why.style.width = w + 'px';
        var left = lr.right + 8;
        if (left + w > window.innerWidth - 12) left = lr.left - 8 - w;
        if (left < 12) { left = Math.max(12, lr.left); why.style.top = (lr.bottom + 6) + 'px'; }
        else why.style.top = Math.min(br.top, window.innerHeight - why.offsetHeight - 12) + 'px';
        why.style.left = left + 'px';
      }
      list.addEventListener('mouseover', function (e) { var b = e.target.closest('.vg-dd__opt'); if (b) explain(b); });
      list.addEventListener('mouseleave', function () { why.hidden = true; });
      list.addEventListener('focusin', function (e) { var b = e.target.closest('.vg-dd__opt'); if (b) explain(b); });
      list.addEventListener('click', function (e) { var b = e.target.closest('.vg-dd__opt'); if (b) choose(Number(b.getAttribute('data-i'))); });
      list.addEventListener('keydown', function (e) {
        var all = Array.prototype.slice.call(list.querySelectorAll('.vg-dd__opt'));
        var at = all.indexOf(document.activeElement);
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          var nx = all[Math.max(0, Math.min(all.length - 1, at + (e.key === 'ArrowDown' ? 1 : -1)))];
          if (nx) { nx.focus({ preventScroll: true }); if (nx.scrollIntoView) nx.scrollIntoView({ block: 'nearest' }); }
        } else if (e.key === 'Escape') {
          e.preventDefault(); e.stopPropagation(); // the Vendor Guide closes its pop-up on Escape; this only closes the list
          closeDd(); btn.focus();
        } else if (e.key === 'Tab') {
          closeDd();
        }
      });
      var o = {
        list: list, why: why, btn: btn,
        away: function (e) { if (!list.contains(e.target) && !btn.contains(e.target)) closeDd(); },
        onScroll: function (e) { if (!list.contains(e.target)) closeDd(); },
      };
      openDd = o;
      btn.setAttribute('aria-expanded', 'true');
      document.addEventListener('mousedown', o.away, true);
      window.addEventListener('scroll', o.onScroll, true);
      window.addEventListener('resize', closeDd);
      var on = list.querySelector('.vg-dd__opt--on') || list.querySelector('.vg-dd__opt');
      if (on) { on.focus({ preventScroll: true }); explain(on); }
    }

    btn.addEventListener('click', function () { if (openDd && openDd.btn === btn) closeDd(); else open(); });
    btn.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); open(); }
    });
  }

  /* #11 - instruction lines read like the PDF. The stored text (with its marks) rides on data-raw, which is what
     custom-vendor-guide.js syncVendorPoInstrHidden saves. */
  function renderInstrItem(span) {
    if (span._vgMarked) return;
    span._vgMarked = true;
    var raw = span.getAttribute('data-raw');
    if (raw == null) { raw = span.textContent || ''; span.setAttribute('data-raw', raw); }
    span.innerHTML = markupHtml(raw);
  }
  function renderInstrLists(root) {
    Array.prototype.slice.call(root.querySelectorAll('.vg-po-instr-text')).forEach(renderInstrItem);
  }
  function previewFor(input) {
    var ed = input.closest('[data-vg-po-instr-editor]');
    if (!ed) return;
    var pv = ed.querySelector('.vg-po-instr-preview');
    if (!pv) {
      pv = document.createElement('div');
      pv.className = 'vg-po-instr-preview';
      var add = ed.querySelector('.vg-po-instr-add');
      if (add) add.parentNode.insertBefore(pv, add.nextSibling);
    }
    var v = input.value || '';
    pv.hidden = v.indexOf('*') === -1;
    pv.innerHTML = pv.hidden ? '' : '<span>Prints as:</span> ' + markupHtml(v);
  }

  /* ---------- assemble ---------- */

  function enhance(root) {
    if (!root) return;
    var panel = root.querySelector('[data-vg-modal-tabpanel="po"]');
    var section = panel && panel.querySelector('.vg-modal__section');
    if (!section || section._vgCards) return;
    section._vgCards = true;
    section.classList.add('vg-pform');

    var grid = document.createElement('div');
    grid.className = 'vg-pcards';
    var used = [];
    var cardEls = {};

    function cardShell(c) {
      var el = document.createElement('section');
      el.className = 'vg-pcard';
      el.setAttribute('data-vg-pcard', c.id);
      el.innerHTML =
        '<button type="button" class="vg-pcard__head" data-vg-pcard-toggle aria-expanded="true">' +
          '<span class="vg-pcard__title">' + esc(c.title) + '</span>' +
          '<span class="vg-pcard__count" data-vg-pcard-count></span>' +
          '<span class="vg-pcard__caret" aria-hidden="true">&#9662;</span>' +
        '</button>' +
        '<div class="vg-pcard__body">' +
          '<p class="vg-pcard__note" data-vg-pcard-note>' + esc(c.note || '') + '</p>' +
          '<div class="vg-pcard__grid"></div>' +
        '</div>';
      return el;
    }

    CARDS.forEach(function (c) {
      var card = cardShell(c);
      var g = card.querySelector('.vg-pcard__grid');
      c.fields.forEach(function (f) {
        var el = fieldEl(section, f);
        if (!el) return;
        var cell = cellOf(el);
        if (!cell || used.indexOf(cell) !== -1) return;
        used.push(cell);
        if (cell.classList.contains('vg-form__row')) cell.classList.add('vg-pcell--full');
        cell.classList.add('vg-pcell');
        g.appendChild(cell);
        if (RELABEL[f]) {
          var lab = cell.querySelector('label[for="' + el.id + '"]');
          if (lab) lab.textContent = RELABEL[f];
        }
      });
      if (g.children.length) { grid.appendChild(card); cardEls[c.id] = card; }
    });

    // Anything this file does not know: an "Other" card, so a new field is never lost from the screen.
    var stray = Array.prototype.slice.call(section.querySelectorAll('[data-vg-po-field]')).filter(function (el) {
      return !grid.contains(el) && !el.closest('.vg-po-copy') && !el.closest('[data-vg-po-instr-editor]');
    });
    if (stray.length) {
      var other = cardShell({ id: 'other', title: 'Other', note: 'Fields not grouped yet.' });
      var og = other.querySelector('.vg-pcard__grid');
      stray.forEach(function (el) {
        var cell = cellOf(el) || el;
        if (used.indexOf(cell) !== -1 || grid.contains(cell)) return;
        used.push(cell);
        cell.classList.add('vg-pcell', 'vg-pcell--full');
        og.appendChild(cell);
      });
      if (og.children.length) { grid.appendChild(other); cardEls.other = other; }
    }

    // The old shells (now-empty rows, the freight fieldset) go; the intro line and the copy-to-others box stay on top.
    Array.prototype.slice.call(section.children).forEach(function (ch) {
      if (ch.classList.contains('vg-po-copy') || ch.classList.contains('vg-form__hint--block')) return;
      if (!ch.querySelector('[data-vg-po-field]')) section.removeChild(ch);
    });
    var intro = section.querySelector('.vg-form__hint--block');
    if (intro) intro.textContent = 'Drives the purchase order and every email we send this supplier. Each card counts the answers an agent needs.';
    section.appendChild(grid);

    Object.keys(cardEls).forEach(function (k) { shortenHints(cardEls[k]); });
    Array.prototype.slice.call(grid.querySelectorAll('select')).forEach(makeSelect);
    renderInstrLists(grid);

    var val = readVal(section);
    // A click slides the card open or shut (window.VGSlide, custom-vendor-guide.js); the first render just sets it.
    function setOpen(card, open, animate) {
      card.classList.toggle('vg-pcard--closed', !open);
      card.querySelector('[data-vg-pcard-toggle]').setAttribute('aria-expanded', open ? 'true' : 'false');
      var body = card.querySelector('.vg-pcard__body');
      if (animate && window.VGSlide) window.VGSlide(body, open); else body.hidden = !open;
    }
    function refresh(initial) {
      var st = cardStatus(val);
      Object.keys(cardEls).forEach(function (id) {
        var card = cardEls[id];
        var s = st[id] || { applies: true, questions: [] };
        var c = countText(s);
        var pill = card.querySelector('[data-vg-pcard-count]');
        pill.textContent = c.text;
        pill.className = 'vg-pcard__count vg-pcard__count--' + c.cls;
        var note = card.querySelector('[data-vg-pcard-note]');
        var base = (CARDS.filter(function (x) { return x.id === id; })[0] || {}).note || '';
        note.textContent = s.applies ? base : (s.reason || base);
        card.classList.toggle('vg-pcard--na', !s.applies);
        // Ryan 2026-10-11: every card starts folded and only a click opens it; the answered pill says enough.
        if (initial) setOpen(card, false);
      });
      // An empty Shipping Note says nothing on a Standard supplier (the PO prints its own line); tuck it away until
      // the arrangement makes it count. A note with words in it always shows.
      var noteEl = fieldEl(section, 'shippingNote');
      if (noteEl) {
        var noteCell = noteEl.closest('.vg-pcell'); // moved into its card by now, so no longer inside its old row
        if (noteCell) noteCell.hidden = (val('shippingModel') || 'standard') === 'standard' && !has(noteEl.value);
      }
    }
    refresh(true);

    section.addEventListener('input', function (e) {
      if (e.target.matches && e.target.matches('[data-vg-po-instr-input]')) previewFor(e.target);
      refresh(false);
    });
    section.addEventListener('change', function () { refresh(false); });
    section.addEventListener('click', function (e) {
      var t = e.target.closest('[data-vg-pcard-toggle]');
      if (t) { var card = t.closest('.vg-pcard'); setOpen(card, card.classList.contains('vg-pcard--closed'), true); return; }
      var q = e.target.closest('.vg-qmark');
      if (q) { e.preventDefault(); if (tipEl && !tipEl.hidden && tipEl._for === q) hideTip(); else { showTip(q); tip()._for = q; } return; }
      // An instruction line added or removed: mark the new line up once custom-vendor-guide.js has put it in.
      if (e.target.closest('[data-vg-po-instr-add]')) setTimeout(function () { renderInstrLists(section); refresh(false); }, 0);
    });
    section.addEventListener('mouseover', function (e) { var q = e.target.closest('.vg-qmark'); if (q) { showTip(q); tip()._for = q; } });
    section.addEventListener('mouseout', function (e) { if (e.target.closest('.vg-qmark')) hideTip(); });
    section.addEventListener('focusin', function (e) { var q = e.target.closest('.vg-qmark'); if (q) { showTip(q); tip()._for = q; } });
    section.addEventListener('focusout', function (e) { if (e.target.closest('.vg-qmark')) hideTip(); });
    section.addEventListener('keydown', function (e) {
      var input = e.target.closest && e.target.closest('[data-vg-po-instr-input]');
      if (input && (e.ctrlKey || e.metaKey) && !e.altKey && (e.key === 'b' || e.key === 'B' || e.key === 'i' || e.key === 'I')) {
        e.preventDefault();
        var r = wrapMark(input.value, input.selectionStart, input.selectionEnd, /b/i.test(e.key) ? '**' : '*');
        input.value = r.value;
        input.setSelectionRange(r.start, r.end);
        previewFor(input);
      }
      if (e.key === 'Enter' && input) setTimeout(function () { renderInstrLists(section); previewFor(input); refresh(false); }, 0);
    });
    var body = root.closest ? root : null;
    if (body) body.addEventListener('scroll', function () { hideTip(); }, { passive: true });
  }

  window.VGPoForm = {
    enhance: enhance,
    closeAll: function () { closeDd(); hideTip(); },
    _test: { splitOption: splitOption, firstSentence: firstSentence, markupHtml: markupHtml, wrapMark: wrapMark, cardStatus: cardStatus, countText: countText, CARDS: CARDS },
  };
})();
/* END CUSTOM_VGUX_BY_CLAUDE */
